import React, { useState } from 'react';
import { FiAlertTriangle, FiX, FiInfo, FiCheck, FiDownload } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';

const StorageWarning: React.FC = () => {
  const { storageWarning, clearStorageWarning, forceCleanupOldConversations, isSidebarCollapsed } = useChat();
  const [cleanupState, setCleanupState] = useState<'idle' | 'processing' | 'success'>('idle');
  const [cleanupResult, setCleanupResult] = useState<{ deletedCount: number; keptCount: number } | null>(null);

  if (!storageWarning?.show) {
    return null;
  }

  const handleCleanup = () => {
    setCleanupState('processing');
    
    setTimeout(() => {
      const result = forceCleanupOldConversations();
      setCleanupResult({ deletedCount: result.deletedCount, keptCount: result.keptCount });
      setCleanupState('success');
      
      setTimeout(() => {
        setCleanupState('idle');
        setCleanupResult(null);
      }, 5000);
    }, 200);
  };

  const getIcon = () => {
    if (!storageWarning.canStore) {
      return <FiAlertTriangle className="text-red-500" size={20} />;
    }
    return <FiInfo className="text-yellow-500" size={20} />;
  };

  const getBackgroundColor = () => {
    if (!storageWarning.canStore) {
      return 'bg-red-100/90 dark:bg-red-900/60 border-red-300 dark:border-red-600';
    }
    return 'bg-yellow-100/30 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-600';
  };

  const getTextColor = () => {
    if (!storageWarning.canStore) {
      return 'text-red-900 dark:text-red-100';
    }
    return 'text-yellow-900 dark:text-yellow-100';
  };

  return (
    <div 
      className={`fixed top-8 z-50 max-w-md w-full mx-4 transition-all duration-300 ease-in-out ${
        isSidebarCollapsed 
          ? 'left-1/2 transform -translate-x-1/2' // Centered when sidebar is collapsed
          : 'left-1/2 md:left-[calc(50%+8rem)] transform -translate-x-1/2' // Centered in chat area (sidebar is 16rem/256px wide)
      }`}
    >
      <div className={`rounded-lg border p-4 shadow-lg backdrop-blur-sm ${getBackgroundColor()}`}>
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium ${getTextColor()}`}>
              Storage Warning
            </p>
            <p className={`text-sm mt-1 ${getTextColor()}`}>
              {storageWarning.message}
            </p>
            {storageWarning.canStore && storageWarning.message.includes('may lead to slowdowns') && (
              <p className={`text-xs mt-2 ${getTextColor()} opacity-90`}>
                Tip: Large conversation history can slow down the app. Use the button below to archive older chats to your computer before removing them from browser storage.
              </p>
            )}
            
            {/* Show cleanup option if storage is getting full */}
            {storageWarning.canStore && (
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={handleCleanup}
                  disabled={cleanupState !== 'idle'}
                  className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 ${
                    cleanupState === 'success'
                      ? 'bg-green-600 text-white'
                      : cleanupState === 'processing'
                      ? 'bg-yellow-500 text-white cursor-not-allowed'
                      : 'bg-yellow-600 text-white hover:bg-yellow-700'
                  }`}
                >
                  {cleanupState === 'success' ? (
                    <>
                      <FiCheck size={12} className="mr-1" />
                      {cleanupResult?.deletedCount === 0 
                        ? 'Storage optimal!' 
                        : `Archived ${cleanupResult?.deletedCount} old chats`
                      }
                    </>
                  ) : cleanupState === 'processing' ? (
                    <>
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin mr-1" />
                      Archiving & cleaning...
                    </>
                  ) : (
                    <>
                      <FiDownload size={12} className="mr-1" />
                      Clear Up & Archive Locally
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          
          <button
            onClick={clearStorageWarning}
            className={`flex-shrink-0 rounded-md p-1.5 hover:bg-black/5 dark:hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              storageWarning.canStore 
                ? 'focus:ring-yellow-500 text-yellow-400' 
                : 'focus:ring-red-500 text-red-400'
            }`}
            aria-label="Dismiss warning"
          >
            <FiX size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default StorageWarning;
